import React from 'react';
import PropTypes from 'prop-types';

import {Button, Box} from 'views/components';
import {translate} from 'views/module';
import StatusTag from './StatusTag';
import AuthenticateUserDialog from './dialogs/AuthenticateUserDialog';
import ConfirmDialog from './dialogs/ConfirmDialog';
import ResetPasswordDialog from './dialogs/ResetPasswordDialog';

const ClientAuthentication = (props) => {
  const {clientBackend, relationshipData, userIdentity} = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState(null);
  const [confirmDialogProps, setConfirmDialogProps] = React.useState({});
  const [form, setForm] = React.useState({
    email: '',
    firstName: '',
    lastName: '',
  });

  const deactivateUser = async () => {
    setIsLoading(true);
    clientBackend.user.deactivateUser(relationshipData.relationshipRn, () => setIsLoading(false));
  };

  const reactivateUser = async () => {
    setIsLoading(true);
    await clientBackend.user.reactivateUser(relationshipData.relationshipRn, () => setIsLoading(false));
  };

  const resetPassword = async (newPassword) => {
    setIsLoading(true);
    await clientBackend.user.resetPassword(relationshipData.relationshipRn, newPassword, () => setIsLoading(false));
  };

  const handleOpenDialog = () => {
    const emailContact = relationshipData.contacts.find((contact) => contact.type === 'Email');
    const email = emailContact ? emailContact.detail.trim() : '';
    const fullName = relationshipData.name || '';
    const firstName = fullName.split(' ')[0] || '';
    const lastName = fullName.split(' ').slice(1).join(' ') || '';
    setForm({email, firstName, lastName});
    setShowDialog(true);
  };
  const handleConfirm = async () => {
    if (confirmAction) {
      await confirmAction();
    }
    setShowConfirmDialog(false);
  };
  const getButtonConfig = () => {
    if (userIdentity?.status === 'ACTIVE') {
      return {
        text: translate('client.ClientDetail.Relationships.Authentication.Deactivate'),
        action: () => {
          setConfirmDialogProps({
            title: translate('client.ClientDetail.Relationships.Authentication.Dialogs.ConfirmDeactivateTitle'),
            message: translate('client.ClientDetail.Relationships.Authentication.Dialogs.ConfirmDeactivateMessage'),
            confirmText: translate('client.ClientDetail.Relationships.Authentication.Deactivate'),
          });
          setConfirmAction(() => deactivateUser);
          setShowConfirmDialog(true);
        },
        showResetPasswordButton: true,
      };
    } else if (userIdentity?.status === 'INACTIVE') {
      return {
        text: translate('client.ClientDetail.Relationships.Authentication.Activate'),
        action: () => {
          setConfirmDialogProps({
            title: translate('client.ClientDetail.Relationships.Authentication.Dialogs.ConfirmActivateTitle'),
            message: translate('client.ClientDetail.Relationships.Authentication.Dialogs.ConfirmActivateMessage'),
            confirmText: translate('client.ClientDetail.Relationships.Authentication.Activate'),
          });
          setConfirmAction(() => reactivateUser);
          setShowConfirmDialog(true);
        },
      };
    } else {
      return {text: translate('client.ClientDetail.Relationships.Authentication.Authenticate'), action: handleOpenDialog};
    }
  };

  const buttonConfig = getButtonConfig();
  const email = userIdentity?.email?.trim();
  return (
    <>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '10px'}}>
        <Box>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
            <Box sx={{fontWeight: 500, fontSize: '14px'}}>{email}</Box>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
            }}
            >
              <StatusTag userIdentity={userIdentity} />
            </Box>
          </Box>
        </Box>
        <Box sx={{display: 'flex', gap: 2}}>
          {
            buttonConfig.showResetPasswordButton &&
            <Button
              onClick={() => setShowResetPasswordDialog(true)}
              variant="outlined"
              color="primary"
              disabled={isLoading}
            >
              {translate('client.ClientDetail.Relationships.Authentication.ResetPassword')}
            </Button>
          }
          <Button
            onClick={buttonConfig.action}
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            {buttonConfig.text}
          </Button>
        </Box>
      </Box>
      {
        showDialog &&
        <AuthenticateUserDialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
          onSubmit={(data) => {
            clientBackend.user.createUser({
              ...data,
              clientRelationshipRn: relationshipData.relationshipRn,
            });
            setShowDialog(false);
          }}
          initialData={form}
          isLoading={isLoading}
        />
      }
      {
        showConfirmDialog &&
        <ConfirmDialog
          open={showConfirmDialog}
          title={confirmDialogProps.title}
          message={confirmDialogProps.message}
          onCancel={() => setShowConfirmDialog(false)}
          onConfirm={handleConfirm}
          confirmText={confirmDialogProps.confirmText}
          cancelText={translate('client.ClientDetail.Relationships.Authentication.Cancel')}
          isLoading={isLoading}
        />
      }
      {
        showResetPasswordDialog &&
        <ResetPasswordDialog
          open={showResetPasswordDialog}
          onClose={() => setShowResetPasswordDialog(false)}
          onSubmit={resetPassword}
          isLoading={isLoading}
        />
      }
    </>
  );
};

ClientAuthentication.propTypes = {
  clientBackend: PropTypes.object.isRequired,
  relationshipData: PropTypes.object.isRequired,
  userIdentity: PropTypes.object,
};

export default ClientAuthentication;
